import React from 'react';
import { format } from 'rut.js';
import { Dropdown } from 'react-bootstrap';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '../../common/Table';
import { Executive, Representative, SigningProcess } from '../types';
import { StatusBadge } from './Badges';
import { DropdownCustomToggle } from '../../common/Dropdown';
import { EllipsisIcon } from '../../FileExplorer/FileExplorerIcons';
import { useControlledModal } from '../../../hooks/useControlledModal';
import { ResendEmailConfirmationModal } from './Modals/ResendEmailConfirmationModal';
import { GenerateNewLinkConfirmationModal } from './Modals/GenerateNewLinkConfirmationModal';
import { downloadBase64File } from '../../../utils/base64';
import { getSignedDocuments } from './backofficeServices';
import { LoadingSpinner } from '../../common';
import { useClipboard } from '../../../hooks/useClipboard';
import { UpdateRepresentativeInformationModal } from './Modals/UpdateRepresentativeInformationModal';
import { Popover } from '../../common/Popover';

type RepresentativeDropdownActions = 'resend-email-confirmation' | 'generate-new-link-confirmation' | 'update-representative-information';

function RepresentativeDropdown({ representative }: { representative: Representative }) {
  if (representative.status === 'SIGNED') return null;
  const { currentAction, setCurrentAction } = useControlledModal<RepresentativeDropdownActions>();
  const { handleCopy } = useClipboard();
  return (
    <>
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          as={DropdownCustomToggle}
          className="file-explorer-dropdown btn-ghost "
          id="file-explorer-breadcrumb-dropdown-toggle"
        >
          <EllipsisIcon className="rotate-90" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="fs-sm">
          <Dropdown.Item
            disabled={representative.status !== 'PROGRESS'}
            // Url will always be present for PROGRESS status
            // So we can assert it's not null
            onClick={() => { handleCopy(representative.url!).catch(console.error); }}
          >
            Copiar link de firma
          </Dropdown.Item>
          <Dropdown.Item
            disabled={['LAPSED', 'SIGNED', 'INIT', 'FAILED'].includes(representative.status)}
            onClick={() => setCurrentAction('resend-email-confirmation')}
          >
            Reenviar correo
          </Dropdown.Item>
          <Dropdown.Item
            disabled={representative.status !== 'LAPSED'}
            onClick={() => setCurrentAction('generate-new-link-confirmation')}
          >
            Generar y enviar nuevo link
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setCurrentAction('update-representative-information')}
          >
            Actualizar información
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <ResendEmailConfirmationModal
        show={currentAction === 'resend-email-confirmation'}
        onHide={() => setCurrentAction(null)}
        data={representative}
      />
      <GenerateNewLinkConfirmationModal
        show={currentAction === 'generate-new-link-confirmation'}
        onHide={() => setCurrentAction(null)}
        data={representative}
      />
      <UpdateRepresentativeInformationModal
        key={`${representative.id}-${representative.representative_email}`}
        show={currentAction === 'update-representative-information'}
        onHide={() => setCurrentAction(null)}
        data={representative}
      />
    </>
  );
}

function RepresentativesList(
  { representatives, isReadOnly, isSuperuser }:
  { representatives: Representative[], isReadOnly: boolean, isSuperuser: boolean },
) {
  const showRepresentativeDropdown = !isReadOnly || isSuperuser;
  return (
    <ul className="list-icon list-icon-check-gray-blue-200 list-icon-centered">
      {representatives.map((representative) => (
        <li
          className={clsx('list-icon-item d-flex gap-2 align-items-center text-start text-gray-800 w-100 justify-content-between', {
            'list-icon-check-success': representative.status === 'SIGNED',
            'list-icon-error': ['LAPSED', 'FAILED'].includes(representative.status),
          })}
          key={representative.id}
        >
          <div>
            <span className="fw-bold d-block ">
              {representative.representative_rut}
            </span>
            <span>
              {representative.representative_email}
            </span>
          </div>
          {showRepresentativeDropdown ? (
            <RepresentativeDropdown representative={representative} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

interface SigningProcessStatusDateProps {
  status: SigningProcess['status'];
  date: string;
}

function SigningProcessStatusDate({ status, date }: SigningProcessStatusDateProps) {
  const formattedExpirationDate = new Date(date).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const label: Record<SigningProcess['status'], string> = {
    INIT: 'Expira el',
    PROGRESS: 'Expira el',
    LAPSED: 'Expiró el',
    SIGNED: 'Firmado el',
    FAILED: 'Falló el',
  };
  return (
    <>
      {label[status]}
      {' '}
      {formattedExpirationDate}
    </>
  );
}

const MAX_DISPLAYED_DOCS = 1;
const LOADING_SPINNER_SIZE = 13;
const LOADING_SPINNER_BORDER_WIDTH = 2;

const formatDocumentDisplayText = (documents: SigningProcess['documents_to_sign']) => {
  const totalCount = documents.length;
  const hasMultipleDocs = totalCount > MAX_DISPLAYED_DOCS;

  if (!hasMultipleDocs) {
    return {
      firstDocType: documents.map((doc) => doc.document_type).join(', '),
      remainingCount: 0,
      remainingCountText: '',
    };
  }

  const firstDocType = documents[0]?.document_type || '';
  const remainingCount = totalCount - MAX_DISPLAYED_DOCS;
  const remainingCountText = remainingCount > 1 ? `y otros ${remainingCount} documentos` : 'y un documento más';
  return {
    firstDocType,
    remainingCount,
    remainingCountText,
  };
};

const renderDocumentList = (documents: SigningProcess['documents_to_sign']) => (
  <>
    <span>Listado de documentos:</span>
    <ul className="text-truncate mb-0">
      {documents.map((doc) => (
        <li key={doc.id}>{doc.document_type}</li>
      ))}
    </ul>
  </>
);

function DownloadSignedDocumentsCell({ row }: { row: SigningProcess }) {
  const { refetch, isFetching } = useQuery({
    queryKey: ['signed-documents', row.id],
    enabled: false,
    queryFn: async () => {
      const response = await getSignedDocuments(row.id);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.filename && data?.data) {
        downloadBase64File(data.data, 'application/zip', data.filename);
      }
    },
  });

  const {
    firstDocType,
    remainingCountText,
  } = formatDocumentDisplayText(row.documents_to_sign);
  const isDownloadDisabled = isFetching || row.status !== 'SIGNED';

  return (
    <div className="d-flex flex-column align-items-start">
      <Popover
        id="docs"
        trigger="hover"
        placement="top-start"
        renderTrigger={() => (
          <span className="fs-sm ps-2 text-start">
            {firstDocType}
            <span className="ms-1 fw-bold">
              {remainingCountText}
            </span>
          </span>
        )}
        renderContent={() => renderDocumentList(row.documents_to_sign)}
      />

      <button
        className="d-inline-flex btn btn-link btn-sm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={() => refetch()}
        disabled={isDownloadDisabled}
        type="button"
      >
        Descargar documentos
        {isFetching && (
          <LoadingSpinner
            size={LOADING_SPINNER_SIZE}
            color="primary"
            borderWidth={LOADING_SPINNER_BORDER_WIDTH}
            className="ms-2"
          />
        )}
      </button>
    </div>
  );
}

function ExecutiveCell({ executive }: { executive: Executive }) {
  return (
    <ul className="list-unstyled ">
      <li className="fw-bold d-block">
        {executive.first_name}
        {' '}
        {executive.last_name}
      </li>
      <li className="fs-xs">{executive.email}</li>
    </ul>
  );
}

export const signingProcessesTableColumns: (
  isReadOnly: boolean,
  isSuperuser: boolean
) => ColumnDef<SigningProcess>[] = (
  isReadOnly: boolean,
  isSuperuser: boolean
) => [
  {
    header: 'Ejecutivo',
    accessorKey: 'executive',
    cell: (row) => {
      if (!row.executive) return null;
      return <ExecutiveCell executive={row.executive} />;
    },
  },

  {
    header: 'RUT de cliente',
    accessorKey: 'entity_rut',
    cell: (row) => (
      <span className="fw-bold d-block ">
        {format(row.entity_rut)}
      </span>
    ),
  },
  {
    header: 'Documentos firmados',
    accessorKey: 'documents',
    cell: (row) => <DownloadSignedDocumentsCell row={row} />,
  },
  {
    header: 'Estado',
    accessorKey: 'status',
    cell: (row) => {
      const date = row.process_completion_date ?? row.current_url_expiration_date;
      return (
        <div>
          <StatusBadge status={row.status} />
          <span className="d-block mt-4">
            {date ? (
              <SigningProcessStatusDate
                status={row.status}
                date={date}
              />
            ) : 'Link pendiente de generación'}

          </span>
        </div>
      );
    },
  },
  {
    header: 'Apoderados',
    accessorKey: 'representatives',
    cell: (row) => (
      <RepresentativesList
        representatives={row.representatives}
        isReadOnly={isReadOnly}
        isSuperuser={isSuperuser}
      />
    ),
  },
];
